import { cn } from "@base/lib/utils";
import ButtonGroup from "@base/ui/ButtonGroup";
import { reservationQueries } from "@features/reservation/api/queryFactory";
import { MeetingRoomDatePicker } from "@features/reservation/ui/MeetingRoomDatePicker";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import AdminDayTimeGrid from "./AdminDayTimeGrid";

export const AdminReservationBoard = () => {
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedRoomId, setSelectedRoomId] = useState<number | null>(null);

  const { data, isLoading, isFetching, isError } = useQuery(
    reservationQueries.meetingRooms(dayjs(selectedDate).format("YYYY-MM-DD")),
  );

  const rooms = data?.rooms ?? [];

  const selectedRoom = useMemo(
    () => rooms.find((r) => r.id === selectedRoomId) ?? rooms[0] ?? null,
    [selectedRoomId, rooms],
  );

  return (
    <div className="space-y-4">
      <h2 className="text-title4 text-gray-1000">{t("adminReservationsTitle")}</h2>

      <div className="my-8 md:my-10">
        <MeetingRoomDatePicker value={selectedDate} onValueChange={setSelectedDate} />
      </div>

      {isLoading ? (
        <div className="py-12 text-center text-gray-500 text-label3">{t("adminLoading")}</div>
      ) : isError ? (
        // 조회 실패를 "회의실 없음"으로 오인하지 않도록 빈 상태와 분리해서 표시한다.
        <div className="py-12 text-center text-red-500 text-label3">{t("adminLoadFailed")}</div>
      ) : rooms.length === 0 ? (
        <div className="py-12 text-center text-gray-500 text-label3">{t("adminNoRooms")}</div>
      ) : (
        <div className={cn("transition-opacity duration-200", isFetching && "opacity-50 pointer-events-none")}>
          {/* 좁은 화면에서는 방을 나란히 놓기 어려워 버튼 그룹으로 한 번에 하나만 보여준다. */}
          <div className="md:hidden">
            <ButtonGroup
              selectedOption={selectedRoom?.id ?? null}
              onOptionChange={(id) => setSelectedRoomId(id)}
              className="mb-4"
              ariaLabel={t("adminSelectRoom")}
            >
              {rooms.map((room) => (
                <ButtonGroup.Button key={room.id} value={room.id} label={room.name} />
              ))}
            </ButtonGroup>
            {selectedRoom && <AdminDayTimeGrid reservations={selectedRoom.reservations} />}
          </div>

          <div className="hidden gap-6 md:flex md:flex-row">
            {rooms.map((room) => (
              <div key={room.id} className="flex-1 min-w-0">
                <div className="flex items-center p-4 mb-2 bg-white border border-gray-300 rounded-8">
                  <h3 className="text-subtitle4 text-gray-1000">{room.name}</h3>
                </div>
                <AdminDayTimeGrid reservations={room.reservations} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
